/*
Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.

Licensed under the Apache License, Version 2.0 (the "License").
You may not use this file except in compliance with the License.
You may obtain a copy of the License at

    http://www.apache.org/licenses/LICENSE-2.0

Unless required by applicable law or agreed to in writing, software
distributed under the License is distributed on an "AS IS" BASIS,
WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
See the License for the specific language governing permissions and
limitations under the License.
*/
import React from 'react';
import { Box, Tooltip } from '@mui/material';

interface EllipsisCellProps {
  content: React.ReactNode;
  tooltip?: React.ReactNode;
  formatContent?: (content: React.ReactNode) => React.ReactNode;
  /**
   * Wrap onto up to this many lines before truncating, instead of clipping to a
   * single line. Long descriptions and names were unreadable at one line: the
   * text was cut after a few words and only a hover tooltip revealed the rest.
   * Omit for the original single-line behaviour.
   */
  maxLines?: number;
}

export const EllipsisCell: React.FC<EllipsisCellProps> = ({
  content,
  tooltip,
  formatContent,
  maxLines,
}) => {
  const formattedContent = formatContent ? formatContent(content) : content;

  // -webkit-line-clamp keeps the "…" affordance while allowing several lines,
  // and caps row height so one long value can't stretch its row far taller than
  // the rest of the table. It is supported in every browser this app targets.
  const clampSx = maxLines && maxLines > 1
    ? {
        display: '-webkit-box',
        WebkitBoxOrient: 'vertical' as const,
        WebkitLineClamp: maxLines,
        overflow: 'hidden',
        // Break inside long unbroken tokens (index names, URLs) so they wrap
        // instead of overflowing the column.
        overflowWrap: 'anywhere' as const,
        whiteSpace: 'normal' as const,
      }
    : {
        whiteSpace: 'nowrap' as const,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
      };

  return (
    <Tooltip title={tooltip || formattedContent}>
      <Box sx={clampSx}>{formattedContent}</Box>
    </Tooltip>
  );
};
